import { useCallback, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

import {
    applyIngredientSupplierTransportSaveSuccesses,
    IngredientSupplierTransportContractError,
    persistIngredientSupplierTransportSelections,
    reconcilePersistedIngredientSupplierRelations,
} from "./ingredientSupplierTransportPersistence";
import type {
    IngredientSupplierTransportDraft,
    IngredientSupplierTransportSelection,
} from "./ingredientSupplierTransportTypes";

type RelationsKey = "suppliers" | "ingredientSuppliers";

type EntityDraft<TKey extends RelationsKey, TRow extends IngredientSupplierTransportDraft> = {
    id?: number;
} & Record<TKey, TRow[]>;

type PersistedRelation = {
    "@id": IRI;
    [key: string]: unknown;
};

type PersistedEntity = {
    id: number;
    relations: PersistedRelation[];
};

type MutationResponse = {
    data?: unknown;
};

type SubmissionOptions<
    TKey extends RelationsKey,
    TDraft extends EntityDraft<TKey, IngredientSupplierTransportDraft>,
> = {
    draft: TDraft;
    setDraftState: Dispatch<SetStateAction<TDraft>>;
    relationsKey: TKey;
    relatedResourceKey: "ingredient" | "supplier";
    getDraftCounterpartIri: (row: TDraft[TKey][number]) => IRI;
    upsert: (draft: TDraft) => Promise<MutationResponse>;
    saveTransportSelections: (
        relationIri: IRI,
        selections?: IngredientSupplierTransportSelection,
    ) => Promise<IngredientSupplierTransportSelection | undefined>;
    onSuccess: (persistedDraft: TDraft) => void;
    onExit: () => void;
    onPersistenceError: () => void;
    onTransportError: () => void;
    onConcurrentEdit: () => void;
};

type SubmissionController<TDraft> = {
    isSubmitting: boolean;
    setDraft: Dispatch<SetStateAction<TDraft>>;
    submit: () => Promise<boolean>;
    confirmExit: () => Promise<void>;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const getIri = (value: unknown): IRI | undefined => {
    if (typeof value === "string") return value as IRI;
    if (!isRecord(value)) return undefined;
    return typeof value["@id"] === "string" ? (value["@id"] as IRI) : undefined;
};

const parseMutationData = (response: MutationResponse): Record<string, unknown> => {
    if (!isRecord(response.data)) {
        throw new IngredientSupplierTransportContractError("The mutation response must expose an entity object.");
    }
    return response.data;
};

const parsePersistedEntityId = (data: Record<string, unknown>): number => {
    const id = data.id;
    if (typeof id !== "number" || !Number.isInteger(id) || id <= 0) {
        throw new IngredientSupplierTransportContractError("The mutation response must expose a persisted entity id.");
    }
    return id;
};

const parsePersistedEntity = (
    data: Record<string, unknown>,
    id: number,
    relationsKey: RelationsKey,
    relatedResourceKey: "ingredient" | "supplier",
): PersistedEntity => {
    const relations = data[relationsKey];
    if (
        !Array.isArray(relations) ||
        relations.some(
            (row) =>
                !isRecord(row) ||
                typeof row["@id"] !== "string" ||
                (row[relatedResourceKey] !== undefined && getIri(row[relatedResourceKey]) === undefined),
        )
    ) {
        throw new IngredientSupplierTransportContractError(
            "The mutation response must expose its ingredient-supplier relations.",
        );
    }

    return {
        id,
        relations: relations as PersistedRelation[],
    };
};

const isExplicitClientRejection = (error: unknown): boolean => {
    const status = (error as { response?: { status?: unknown } })?.response?.status;
    return typeof status === "number" && status >= 400 && status < 500;
};

const mergePersistedEntity = <
    TKey extends RelationsKey,
    TDraft extends EntityDraft<TKey, IngredientSupplierTransportDraft>,
>(
    current: TDraft,
    persisted: PersistedEntity,
    relationsKey: TKey,
    getDraftCounterpartIri: (row: TDraft[TKey][number]) => IRI,
    relatedResourceKey: "ingredient" | "supplier",
): TDraft => ({
    ...current,
    id: current.id ?? persisted.id,
    [relationsKey]: current[relationsKey].map((row: TDraft[TKey][number]) => {
        if (row["@id"] !== undefined) return row;

        const counterpartIri = getDraftCounterpartIri(row);
        const matches = persisted.relations.filter(
            (persistedRow) => getIri(persistedRow[relatedResourceKey]) === counterpartIri,
        );
        const relationIri = matches.length === 1 ? getIri(matches[0]) : undefined;
        return relationIri === undefined ? row : { ...row, "@id": relationIri };
    }),
});

export const useIngredientSupplierTransportSubmission = <
    TKey extends RelationsKey,
    TDraft extends EntityDraft<TKey, IngredientSupplierTransportDraft>,
>({
    draft,
    setDraftState,
    relationsKey,
    relatedResourceKey,
    getDraftCounterpartIri,
    upsert,
    saveTransportSelections,
    onSuccess,
    onExit,
    onPersistenceError,
    onTransportError,
    onConcurrentEdit,
}: SubmissionOptions<TKey, TDraft>): SubmissionController<TDraft> => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submissionInFlight = useRef(false);
    const createdEntityId = useRef<number | undefined>(undefined);
    const persistenceIdentityIsUnknown = useRef(false);
    const draftRevision = useRef(0);
    const persistedRelationIris = useRef(new Map<IRI, IRI>());

    const setDraft = useCallback<Dispatch<SetStateAction<TDraft>>>(
        (update) => {
            draftRevision.current += 1;
            setDraftState(update);
        },
        [setDraftState],
    );

    const submit = async (): Promise<boolean> => {
        if (submissionInFlight.current) return false;

        submissionInFlight.current = true;
        setIsSubmitting(true);
        const submittedRevision = draftRevision.current;
        const draftWithEntityIdentity =
            draft.id === undefined && createdEntityId.current !== undefined ?
                ({ ...draft, id: createdEntityId.current } as TDraft)
            :   draft;
        const submittedDraft = {
            ...draftWithEntityIdentity,
            [relationsKey]: draftWithEntityIdentity[relationsKey].map((row: TDraft[TKey][number]) => {
                if (row["@id"] !== undefined) return row;
                const knownRelationIri = persistedRelationIris.current.get(getDraftCounterpartIri(row));
                return knownRelationIri === undefined ? row : { ...row, "@id": knownRelationIri };
            }),
        } as TDraft;

        try {
            if (persistenceIdentityIsUnknown.current) {
                onPersistenceError();
                return false;
            }

            let persistedDraft: TDraft;
            try {
                const responseData = parseMutationData(await upsert(submittedDraft));
                const persistedEntityId = parsePersistedEntityId(responseData);
                if (submittedDraft.id === undefined) {
                    createdEntityId.current = persistedEntityId;
                } else if (persistedEntityId !== submittedDraft.id) {
                    throw new IngredientSupplierTransportContractError(
                        `Expected persisted entity ${submittedDraft.id}; received ${persistedEntityId}.`,
                    );
                }
                const persistedEntity = parsePersistedEntity(
                    responseData,
                    persistedEntityId,
                    relationsKey,
                    relatedResourceKey,
                );

                const reconciledRows = reconcilePersistedIngredientSupplierRelations({
                    rows: submittedDraft[relationsKey],
                    persistedRows: persistedEntity.relations,
                    getDraftCounterpartIri,
                    getPersistedCounterpartIri: (row) => getIri(row[relatedResourceKey]),
                });
                persistedDraft = {
                    ...submittedDraft,
                    id: persistedEntity.id,
                    [relationsKey]: reconciledRows,
                };
                persistedRelationIris.current = new Map(
                    reconciledRows.flatMap((row) =>
                        row["@id"] === undefined ? [] : [[getDraftCounterpartIri(row), row["@id"]]],
                    ),
                );
                setDraftState((current) =>
                    mergePersistedEntity(
                        current,
                        persistedEntity,
                        relationsKey,
                        getDraftCounterpartIri,
                        relatedResourceKey,
                    ),
                );
            } catch (error) {
                const containsUnidentifiedRelation = submittedDraft[relationsKey].some(
                    (row) => row["@id"] === undefined,
                );
                if (
                    error instanceof IngredientSupplierTransportContractError ||
                    (!isExplicitClientRejection(error) &&
                        (submittedDraft.id === undefined || containsUnidentifiedRelation))
                ) {
                    persistenceIdentityIsUnknown.current = true;
                }
                onPersistenceError();
                return false;
            }

            const { successes, failures } = await persistIngredientSupplierTransportSelections(
                persistedDraft[relationsKey],
                saveTransportSelections,
            );
            if (successes.length > 0) {
                setDraftState((current) => ({
                    ...current,
                    [relationsKey]: applyIngredientSupplierTransportSaveSuccesses(current[relationsKey], successes),
                }));
            }
            if (failures.length > 0) {
                onTransportError();
                return false;
            }

            if (draftRevision.current !== submittedRevision) {
                onConcurrentEdit();
                return false;
            }

            onSuccess(persistedDraft);
            return true;
        } finally {
            submissionInFlight.current = false;
            setIsSubmitting(false);
        }
    };

    const confirmExit = async (): Promise<void> => {
        if (submissionInFlight.current) return;
        onExit();
    };

    return { isSubmitting, setDraft, submit, confirmExit };
};
